import { useMutation } from "@tanstack/react-query";
import { api } from "./api";

// Mirrors apps/api/src/lib/import/leads-import.ts's shapes 1:1 (raw Zod-free
// objects, same as project-tracking.ts's convention for this API).

export interface LeadsMapping {
  email: string | null;
  phone: string | null;
  first_name: string | null;
  last_name: string | null;
  /**
   * TASK-155 — optional, unlike Sales' mandatory event_date. Unmapped: the
   * whole batch keeps one import-time timestamp (behaviour before TASK-155).
   * Mapped: the date becomes mandatory per row.
   */
  event_date: string | null;
}

export const EMPTY_LEADS_MAPPING: LeadsMapping = {
  email: null,
  phone: null,
  first_name: null,
  last_name: null,
  event_date: null,
};

// TASK-19: "duplicate" was missing here even though the API already returns
// it (see leads-import.ts's LeadRowStatus) — the sample-row label below
// mislabeled duplicates as an unlabeled "Fehler".
export type LeadRowStatus = "new" | "existing" | "duplicate" | "error";
export interface PreviewRow {
  row: number;
  email: string | null;
  phone: string | null;
  first_name: string | null;
  last_name: string | null;
  /** TASK-155 — ISO datetime, or null when no date column is mapped. */
  event_date: string | null;
  valid: boolean;
  reason?: "missing_email" | "invalid_email" | "missing_event_date" | "invalid_event_date";
  status: LeadRowStatus;
}

export interface LeadsPreviewResult {
  headers: string[];
  delimiter: "," | ";";
  mappingValid: boolean;
  counts: { new: number; existing: number; duplicates: number; errors: number };
  total: number;
  sample: PreviewRow[];
}

export function useLeadsPreview(projectId: number) {
  return useMutation({
    mutationFn: (input: { csv: string; mapping: LeadsMapping; statusFilter?: LeadRowStatus }) =>
      api<LeadsPreviewResult>(`/admin/projects/${projectId}/import/preview`, {
        method: "POST",
        body: JSON.stringify(input),
      }),
  });
}

export interface CommitLeadsResponse {
  ok: true;
  batch_id: number;
  imported: number;
  skipped_duplicates: number;
  errors: number;
  total: number;
}

export function useCommitLeadsImport(projectId: number) {
  return useMutation({
    mutationFn: (input: {
      csv: string;
      mapping: LeadsMapping;
      legalBasisConfirmed: boolean;
      label?: string;
      eventType?: string;
      sendToMeta?: boolean;
    }) =>
      api<CommitLeadsResponse>(`/admin/projects/${projectId}/import/commit`, {
        method: "POST",
        body: JSON.stringify(input),
      }),
  });
}
